import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useTaxYear } from "../contexts/TaxYearContext";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import ApprovalFilterBanner from "../components/ApprovalFilterBanner";
import { FiscalYearBadge } from "../components/TaxYearBadge";
import FySyncBanner from "../components/FySyncBanner";
import ArchiveButton from "../components/ArchiveButton";
import ReportActions from "../components/ReportActions";
import { Calculator, Building2, Save, Loader2, Info, AlertCircle, FileBarChart2, Download, BookOpen, Calendar } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(n || 0);
const fmt2 = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n || 0);
const COLORS = ["#7C3AED", "#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

const CACFP_INCOME_CATS = ["CACFP Income"];
const CACFP_EXPENSE_CATS = ["CACFP Food Expense", "CACFP Non-Food Expense", "CACFP Utilities", "CACFP Use Charge for Space"];

const TABS = [
  { id: "overview", label: "Overview", icon: FileBarChart2 },
  { id: "allocation", label: "Allocation Formula", icon: Calculator },
  { id: "use_charge", label: "Use Charge Worksheet", icon: Building2 },
  { id: "ledger", label: "Monthly Ledger", icon: BookOpen },
  { id: "food_labor", label: "Food & Labor", icon: Calculator },
];

async function downloadPdf(url, filename) {
  try {
    const res = await axios.get(url, { responseType: "blob", withCredentials: true });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(link.href);
  } catch (err) {
    alert("Download failed — please make sure you've saved the worksheet first.");
  }
}

const DEFAULT_USE_CHARGE_ROWS = [
  { category: "Rent / Lease", monthly_cost: 0, months_per_year: 12 },
  { category: "Utilities", monthly_cost: 0, months_per_year: 12 },
  { category: "Trash Removal", monthly_cost: 0, months_per_year: 12 },
  { category: "Insurance", monthly_cost: 0, months_per_year: 12 },
  { category: "Maintenance / Repairs", monthly_cost: 0, months_per_year: 12 },
  { category: "Other", monthly_cost: 0, months_per_year: 12 },
];

export default function CACFPOverview() {
  const { user } = useAuth();
  const { defaultTaxYear } = useTaxYear();
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default Tax Year Resolver applied to the CACFP fiscal-year badge.
  // Taxes (and CACFP reconciliation work) are done in arrears — after the FY
  // is complete. Show the most recently COMPLETED fiscal year, not the
  // in-progress one.
  //
  // • Calendar mode: CACFP FY runs Jan–Dec and equals the tax year → use
  //   defaultTaxYear from the resolver (= the prior completed tax year).
  // • USDA mode: CACFP FY runs Oct–Sept. FY YYYY ends Sept 30, YYYY. So:
  //     - On or after Oct 1 → the FY that just ended has number = calendar year
  //     - Before Oct 1 → the FY that just ended has number = calendar year - 1
  const _now = new Date();
  const currentFy = (user?.cacfp_fy_mode === "usda_fy")
    ? (_now.getMonth() + 1 >= 10 ? _now.getFullYear() : _now.getFullYear() - 1)
    : defaultTaxYear;

  useEffect(() => {
    axios.get(`${API}/transactions/summary`, { withCredentials: true })
      .then(res => setSummary(res.data))
      .catch(err => console.error("Summary fetch error:", err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="cacfp-page">
      <FySyncBanner fiscalYear={currentFy} />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>CACFP Center</h1>
            {user?.cacfp_participant && (
              <FiscalYearBadge fy={currentFy} mode={user?.cacfp_fy_mode || "calendar"} />
            )}
          </div>
          <p className="text-slate-500 text-sm mt-1">
            Child and Adult Care Food Program — overview, allocation formula, and use-charge worksheet.
          </p>
        </div>
        {user?.cacfp_participant && (
          <ReportActions
            taxYear={currentFy}
            category="CACFP Overview"
            source="/api/cacfp/overview/pdf"
            sourceParams={{ fiscal_year: currentFy }}
            titleSuffix={`FY ${currentFy}`}
            filenameHint={`cacfp-overview-fy${currentFy}.pdf`}
            testId="cacfp-overview"
          />
        )}
      </div>

      {/* Ship Gate 0a — non-participant / pending banner */}
      {!user?.cacfp_participant && (
        <div
          data-testid={user?.cacfp_application_pending ? "cacfp-pending-banner" : "cacfp-nonparticipant-banner"}
          className={`flex items-start gap-3 rounded-xl p-4 border-2 ${
            user?.cacfp_application_pending
              ? "bg-amber-50 border-amber-300"
              : "bg-slate-50 border-slate-200"
          }`}
        >
          <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${user?.cacfp_application_pending ? "text-amber-600" : "text-slate-500"}`} />
          <div className="flex-1">
            <p className={`text-sm font-bold ${user?.cacfp_application_pending ? "text-amber-900" : "text-slate-800"}`} style={{ fontFamily: "Manrope" }}>
              {user?.cacfp_application_pending
                ? "Your CACFP application is marked Pending"
                : "You're not currently a CACFP participant"}
            </p>
            <p className={`text-xs mt-1 leading-relaxed ${user?.cacfp_application_pending ? "text-amber-800" : "text-slate-600"}`}>
              {user?.cacfp_application_pending
                ? "Once your sponsor approves you, switch to Active in Settings → CACFP. Until then, calculators are read-only and food-program totals are not merged into Tax Prep."
                : "Allocation, Use Charge, Monthly Ledger, and Food & Labor tools are designed for CACFP-enrolled centers. If you join CACFP, enable it in Settings → CACFP to unlock these tools and merge them into your Tax Prep."}
            </p>
            <a
              href="/app/settings?tab=cacfp"
              data-testid="cacfp-banner-settings-link"
              className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-800 mt-2"
            >
              Go to CACFP settings →
            </a>
          </div>
        </div>
      )}

      {/* Tab strip */}
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`cacfp-tab-${id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all ${
              tab === id ? "border-purple-600 text-purple-700" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab summary={summary} loading={loading} />}
      {tab === "allocation" && <AllocationTab user={user} />}
      {tab === "use_charge" && <UseChargeTab user={user} />}
      {tab === "ledger" && <LedgerTab />}
      {tab === "food_labor" && <FoodLaborTab />}

      {/* Universal disclaimer footer for every CACFP page */}
      <div className="mt-8 text-xs text-slate-400 italic border-t border-slate-100 pt-4" data-testid="cacfp-disclaimer">
        For organizational and bookkeeping purposes only. Not tax advice. References FNS Instruction 796-2 Rev. 4, IRS Pub 587, and IRS Form 8829. Always consult a licensed CPA.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 1 — Overview (existing functionality preserved 1:1)
// ─────────────────────────────────────────────────────────────────────────────
function OverviewTab({ summary, loading }) {
  const getVal = (key, src) => summary?.[src]?.[key] || 0;
  const totalCACFPIncome = CACFP_INCOME_CATS.reduce((s, k) => s + getVal(k, "income_sources"), 0);
  const totalCACFPExpenses = CACFP_EXPENSE_CATS.reduce((s, k) => s + getVal(k, "expense_breakdown"), 0);
  const netCACFP = totalCACFPIncome - totalCACFPExpenses;
  const expenseChartData = CACFP_EXPENSE_CATS.map(k => ({ name: k, value: getVal(k, "expense_breakdown") })).filter(d => d.value > 0);

  if (loading) return (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse" />)}
    </div>
  );

  return (
    <div className="space-y-6">
      <ApprovalFilterBanner summary={summary} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <KpiCard label="Total CACFP Income" value={totalCACFPIncome} hint="CACFP-reimbursed income only" testId="cacfp-total-income" color="text-emerald-600" />
        <KpiCard label="Total CACFP Expenses" value={totalCACFPExpenses} hint="Food, non-food, utilities & more" testId="cacfp-total-expenses" color="text-red-600" />
        <KpiCard label="Net CACFP" value={netCACFP} hint={netCACFP >= 0 ? "Profit" : "Loss"} testId="cacfp-net" color={netCACFP >= 0 ? "text-emerald-600" : "text-red-600"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h2 className="text-base font-bold text-slate-900 mb-3" style={{ fontFamily: "Manrope" }}>Income Breakdown</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-slate-500 uppercase font-semibold border-b border-slate-100"><th className="text-left py-2">Category</th><th className="text-right py-2">Amount</th><th className="text-right py-2">%</th></tr></thead>
            <tbody>{CACFP_INCOME_CATS.map(k => { const v = getVal(k, "income_sources"); const pct = totalCACFPIncome > 0 ? ((v / totalCACFPIncome) * 100).toFixed(1) : 0; return (<tr key={k} className="border-b border-slate-50"><td className="py-2 text-slate-700">{k}</td><td className="text-right text-slate-900 font-semibold">{fmt(v)}</td><td className="text-right text-slate-400">{pct}%</td></tr>); })}</tbody>
          </table>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h2 className="text-base font-bold text-slate-900 mb-3" style={{ fontFamily: "Manrope" }}>Expense Breakdown</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-slate-500 uppercase font-semibold border-b border-slate-100"><th className="text-left py-2">Category</th><th className="text-right py-2">Amount</th><th className="text-right py-2">%</th></tr></thead>
            <tbody>{CACFP_EXPENSE_CATS.map(k => { const v = getVal(k, "expense_breakdown"); const pct = totalCACFPExpenses > 0 ? ((v / totalCACFPExpenses) * 100).toFixed(1) : 0; return (<tr key={k} className="border-b border-slate-50"><td className="py-2 text-slate-700">{k}</td><td className="text-right text-slate-900 font-semibold">{fmt(v)}</td><td className="text-right text-slate-400">{pct}%</td></tr>); })}</tbody>
          </table>
        </div>
      </div>

      {expenseChartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <h2 className="text-base font-bold text-slate-900 mb-3" style={{ fontFamily: "Manrope" }}>Expense Distribution</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={expenseChartData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`} outerRadius={100} fill="#8884d8" dataKey="value">{expenseChartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie>
                <Tooltip formatter={(v) => fmt(v)} /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <h2 className="text-base font-bold text-slate-900 mb-3" style={{ fontFamily: "Manrope" }}>Income Sources</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[{ name: "CACFP", income: totalCACFPIncome }]}>
                <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} /><Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="income" fill="#7C3AED" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, hint, testId, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={testId}>
      <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider mb-2">{label}</p>
      <p className={`text-3xl font-bold ${color}`} style={{ fontFamily: "Manrope" }}>{fmt(value)}</p>
      <p className="text-xs text-slate-400 mt-1">{hint}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 2 — Allocation Formula (auto-routes Space-only OR Time-Space)
// ─────────────────────────────────────────────────────────────────────────────
function AllocationTab({ user }) {
  const method = user?.allocation_method;
  const [allocation, setAllocation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Center mode inputs (Ship Gate 1a — line-item CACFP spaces per FNS 796-2 Rev. 4)
  const DEFAULT_CACFP_SPACES = [
    { description: "Kitchen", sq_ft: "" },
    { description: "Dining area", sq_ft: "" },
    { description: "Indoor classroom eating zone", sq_ft: "" },
    { description: "Outdoor eating area (playground / gazebo)", sq_ft: "" },
    { description: "Hand-washing station", sq_ft: "" },
    { description: "Food storage / pantry", sq_ft: "" },
  ];
  const [cacfpSpaces, setCacfpSpaces] = useState(DEFAULT_CACFP_SPACES);
  const [totalSqft, setTotalSqft] = useState("");
  // Legacy mirror — kept for backward compat with anything that still reads them
  const [diningSqft, setDiningSqft] = useState("");
  const [kitchenSqft, setKitchenSqft] = useState("");

  // Time-Space mode inputs
  const [daycareSqft, setDaycareSqft] = useState("");
  const [totalHomeSqft, setTotalHomeSqft] = useState("");
  const [hoursPerDay, setHoursPerDay] = useState("10");
  const [daysPerWeek, setDaysPerWeek] = useState("5");
  const [weeksPerYear, setWeeksPerYear] = useState("50");

  // Ship Gate 1b — FY picker state
  const [yearsList, setYearsList] = useState([]);
  const [selectedFy, setSelectedFy] = useState(null);
  const [currentFy, setCurrentFy] = useState(null);
  const [fyMode, setFyMode] = useState("calendar");

  // Load the list of FY years the user has allocations for + current FY
  useEffect(() => {
    axios.get(`${API}/cacfp/allocation/years`, { withCredentials: true })
      .then(({ data }) => {
        setYearsList(data.years || []);
        setCurrentFy(data.current_fy);
        setFyMode(data.fy_mode || "calendar");
        if (selectedFy == null) setSelectedFy(data.current_fy);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllocation = useCallback(async (fy) => {
    try {
      const targetFy = fy ?? selectedFy;
      const url = targetFy != null ? `${API}/cacfp/allocation?fiscal_year=${targetFy}` : `${API}/cacfp/allocation`;
      const { data } = await axios.get(url, { withCredentials: true });
      if (data && data.method) {
        setAllocation(data);
        if (data.method === "cacfp_space") {
          setDiningSqft(String(data.sq_ft_dining || ""));
          setKitchenSqft(String(data.sq_ft_kitchen_storage || ""));
          setTotalSqft(String(data.sq_ft_total || ""));
          if (Array.isArray(data.cacfp_spaces) && data.cacfp_spaces.length > 0) {
            setCacfpSpaces(data.cacfp_spaces.map(s => ({ description: s.description || "", sq_ft: String(s.sq_ft || "") })));
          } else if (data.sq_ft_dining || data.sq_ft_kitchen_storage) {
            const seeded = [];
            if (data.sq_ft_kitchen_storage) seeded.push({ description: "Kitchen / Food storage", sq_ft: String(data.sq_ft_kitchen_storage) });
            if (data.sq_ft_dining) seeded.push({ description: "Dining area", sq_ft: String(data.sq_ft_dining) });
            setCacfpSpaces(seeded);
          } else {
            setCacfpSpaces(DEFAULT_CACFP_SPACES);
          }
        } else {
          setDaycareSqft(String(data.sq_ft_daycare || ""));
          setTotalHomeSqft(String(data.sq_ft_total_home || ""));
          setHoursPerDay(String(data.hours_per_day || 10));
          setDaysPerWeek(String(data.days_per_week || 5));
          setWeeksPerYear(String(data.weeks_per_year || 50));
        }
      } else {
        // No allocation saved yet for this FY — reset to defaults
        setAllocation(null);
        setCacfpSpaces(DEFAULT_CACFP_SPACES);
        setTotalSqft("");
      }
    } catch (e) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFy]);

  useEffect(() => { if (selectedFy != null) loadAllocation(selectedFy); }, [selectedFy, loadAllocation]);

  const handleSave = async () => {
    setError("");
    setBusy(true);
    try {
      let res;
      if (method === "time_space") {
        res = await axios.post(`${API}/cacfp/allocation/time-space`, {
          sq_ft_daycare: Number(daycareSqft),
          sq_ft_total_home: Number(totalHomeSqft),
          hours_per_day: Number(hoursPerDay),
          days_per_week: Number(daysPerWeek),
          weeks_per_year: Number(weeksPerYear),
          fiscal_year: selectedFy,
        }, { withCredentials: true });
      } else {
        const items = cacfpSpaces
          .map(s => ({ description: (s.description || "").trim(), sq_ft: Number(s.sq_ft) || 0 }))
          .filter(s => s.description && s.sq_ft > 0);
        if (items.length === 0) {
          setError("Add at least one CACFP space with sq ft > 0");
          setBusy(false);
          return;
        }
        res = await axios.post(`${API}/cacfp/allocation/center`, {
          cacfp_spaces: items,
          sq_ft_total: Number(totalSqft),
          sq_ft_dining: 0,
          sq_ft_kitchen_storage: 0,
          fiscal_year: selectedFy,
        }, { withCredentials: true });
      }
      setAllocation(res.data);
      // Refresh year list so the newly-saved FY is included in the picker
      try {
        const { data: yrs } = await axios.get(`${API}/cacfp/allocation/years`, { withCredentials: true });
        setYearsList(yrs.years || []);
      } catch {/* non-fatal */}
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!method) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3" data-testid="allocation-no-setup">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-amber-900">Please complete the entity setup first.</p>
          <p className="text-xs text-amber-700 mt-1">Log out and back in to trigger the setup wizard, or contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="allocation-tab">
      {/* Ship Gate 1b — Fiscal Year picker */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4 flex-wrap" data-testid="fy-picker-bar">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <span className="text-xs uppercase tracking-wider font-bold text-slate-500">Fiscal Year</span>
        </div>
        <select
          data-testid="fy-picker-select"
          value={selectedFy ?? ""}
          onChange={(e) => setSelectedFy(Number(e.target.value))}
          className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          {yearsList.map((y) => (
            <option key={y} value={y}>
              {fyMode === "usda_fy" ? `USDA FY ${y} (Oct ${y - 1} – Sept ${y})` : `${y} (Jan – Dec)`}{y === currentFy ? "  • current" : ""}
            </option>
          ))}
        </select>
        <button
          data-testid="fy-add-new"
          onClick={() => {
            const last = yearsList[0] || (currentFy || new Date().getFullYear());
            const next = last + 1;
            if (!yearsList.includes(next)) {
              setYearsList([next, ...yearsList]);
            }
            setSelectedFy(next);
          }}
          className="text-xs font-bold text-purple-700 hover:text-purple-900"
        >
          + Add next year
        </button>
        <span className="text-[11px] text-slate-400 ml-auto leading-tight">
          FNS 796-2 Rev. 4 allows your operator-chosen fiscal year.
          {fyMode === "calendar" ? " You're using Calendar Year." : " You're using USDA Federal FY."}
        </span>
      </div>

      {/* Formula box (navy) — matches reference style */}
      <div className="bg-slate-900 text-white rounded-xl p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-purple-300 mb-2">Formula — FNS § VIII I 36 c(2)</p>
        {method === "time_space" ? (
          <>
            <p className="text-base font-bold" style={{ fontFamily: "Manrope" }}>Time-Space %</p>
            <p className="text-sm text-slate-200 mt-2 leading-relaxed">
              Time % = (Hours/Day × Days/Week × Weeks/Year) ÷ 8,760 hours per year<br />
              Space % = Daycare Sq Ft ÷ Total Home Sq Ft<br />
              <strong className="text-purple-300">T/S % = Time % × Space %</strong>
            </p>
          </>
        ) : (
          <>
            <p className="text-base font-bold" style={{ fontFamily: "Manrope" }}>CACFP Space %</p>
            <p className="text-sm text-slate-200 mt-2 leading-relaxed">
              <strong className="text-purple-300">Allocation % = (Dining Sq Ft + Kitchen/Food Storage Sq Ft) ÷ Total Center Sq Ft × 100</strong>
            </p>
          </>
        )}
      </div>

      {/* Example box (yellow) */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="allocation-example">
        <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2">Example</p>
        {method === "time_space" ? (
          <p className="text-sm text-amber-900 leading-relaxed">
            Daycare 800 sq ft ÷ Total home 2,000 sq ft = <strong>40% Space</strong>.<br />
            Hours: 10 × 5 × 50 = 2,500 ÷ 8,760 = <strong>28.5% Time</strong>.<br />
            T/S % = 40% × 28.5% = <strong>11.4%</strong> deductible.
          </p>
        ) : (
          <p className="text-sm text-amber-900 leading-relaxed">
            Dining 6,080 + Kitchen 600 = 6,680 sq ft CACFP. Total center 9,600 sq ft.<br />
            Allocation % = 6,680 ÷ 9,600 = <strong>69.58%</strong>.
          </p>
        )}
      </div>

      {/* Inputs */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <p className="text-sm font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope" }}>Your Inputs</p>

        {method === "time_space" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <NumberField label="Daycare Sq Ft" value={daycareSqft} onChange={setDaycareSqft} testId="ts-daycare-sqft" />
            <NumberField label="Total Home Sq Ft" value={totalHomeSqft} onChange={setTotalHomeSqft} testId="ts-total-sqft" />
            <NumberField label="Hours / Day" value={hoursPerDay} onChange={setHoursPerDay} testId="ts-hours" />
            <NumberField label="Days / Week" value={daysPerWeek} onChange={setDaysPerWeek} testId="ts-days" />
            <NumberField label="Weeks / Year" value={weeksPerYear} onChange={setWeeksPerYear} testId="ts-weeks" />
          </div>
        ) : (
          <div data-testid="ctr-spaces-table" className="space-y-3">
            <p className="text-xs text-slate-500 mb-2 leading-relaxed">
              List every space where CACFP operates (kitchen, indoor dining, classroom eating zones, outdoor eating areas, hand-washing stations, food storage). Per <strong>FNS 796-2 Rev. 4</strong>, every CACFP-eligible space counts.
            </p>
            <div className="overflow-hidden border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold w-2/3">Space Description</th>
                    <th className="text-right px-3 py-2 font-semibold w-1/4">Sq Ft</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {cacfpSpaces.map((s, idx) => (
                    <tr key={idx} className="border-t border-slate-100">
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          value={s.description}
                          data-testid={`ctr-space-desc-${idx}`}
                          onChange={(e) => setCacfpSpaces(prev => prev.map((row, i) => i === idx ? { ...row, description: e.target.value } : row))}
                          placeholder="e.g. Playground gazebo"
                          className="w-full text-sm border border-transparent focus:border-purple-400 focus:bg-white rounded px-2 py-1.5 outline-none"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={s.sq_ft}
                          data-testid={`ctr-space-sqft-${idx}`}
                          onChange={(e) => setCacfpSpaces(prev => prev.map((row, i) => i === idx ? { ...row, sq_ft: e.target.value } : row))}
                          placeholder="0"
                          className="w-full text-right text-sm border border-transparent focus:border-purple-400 focus:bg-white rounded px-2 py-1.5 outline-none"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          onClick={() => setCacfpSpaces(prev => prev.filter((_, i) => i !== idx))}
                          data-testid={`ctr-space-remove-${idx}`}
                          className="text-slate-300 hover:text-red-500 transition-colors"
                          aria-label="Remove row"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200 font-bold text-slate-800">
                    <td className="px-3 py-2 text-right">Total CACFP Sq Ft:</td>
                    <td className="px-3 py-2 text-right" data-testid="ctr-spaces-subtotal">
                      {cacfpSpaces.reduce((s, r) => s + (Number(r.sq_ft) || 0), 0).toLocaleString()}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="flex items-center justify-between gap-3">
              <button
                onClick={() => setCacfpSpaces(prev => [...prev, { description: "", sq_ft: "" }])}
                data-testid="ctr-space-add"
                className="flex items-center gap-1 text-xs font-bold text-purple-700 hover:text-purple-900"
              >
                + Add another space
              </button>
            </div>
            <div className="mt-4 max-w-xs">
              <NumberField label="Total Center Sq Ft (whole building)" value={totalSqft} onChange={setTotalSqft} testId="ctr-total" />
            </div>
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={busy}
          data-testid="allocation-save-btn"
          className="mt-5 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white text-sm font-bold px-6 py-2.5 rounded-lg transition-all flex items-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {busy ? "Saving…" : "Save Allocation"}
        </button>
        {error && typeof error === "string" && (
          <p className="text-xs text-red-600 mt-3" data-testid="allocation-error">{error}</p>
        )}
      </div>

      {/* Result */}
      {allocation && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5" data-testid="allocation-result">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Your Allocation %</p>
              <p className="text-5xl font-extrabold text-emerald-700" style={{ fontFamily: "Manrope" }}>
                {allocation.allocation_pct}%
              </p>
              {allocation.method === "time_space" && (
                <p className="text-xs text-emerald-700 mt-2">
                  Time %: {allocation.time_pct}% · Space %: {allocation.space_pct}%
                </p>
              )}
              <p className="text-xs text-slate-500 mt-3">
                Saved {allocation.effective_date ? new Date(allocation.effective_date).toLocaleDateString() : "now"}.
                This % is now used in your Use Charge Worksheet.
              </p>
            </div>
            <button
              onClick={() => downloadPdf(`${API}/cacfp/allocation/pdf`, `cacfp-allocation-${new Date().toISOString().slice(0,10)}.pdf`)}
              data-testid="allocation-download-pdf"
              className="flex items-center gap-2 bg-white border-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-bold px-4 py-2 rounded-lg transition-all"
            >
              <Download className="w-4 h-4" /> Download PDF
            </button>
            <ArchiveButton
              taxYear={new Date().getFullYear()}
              category="CACFP Allocation"
              source="/api/cacfp/allocation/pdf"
              sourceParams={{}}
              titleSuffix={`${allocation.allocation_pct}%`}
              testId="cacfp-allocation"
            />
          </div>
        </div>
      )}

      <Tips>
        <li>Keep your sq ft figures consistent year-over-year (FNS § VII A).</li>
        <li>If your sq ft changes (e.g., renovation), update here — Use Charge Worksheet auto-recalculates.</li>
        {method === "time_space" && <li>Hours include all daycare-related time: business calls, prep, cleaning, recordkeeping.</li>}
      </Tips>
    </div>
  );
}

function NumberField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
      />
    </div>
  );
}

function Tips({ children }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-2 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Tips</p>
      <ul className="text-xs text-blue-900 space-y-1 list-disc list-inside">{children}</ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 3 — Use Charge Worksheet
// ─────────────────────────────────────────────────────────────────────────────
function UseChargeTab() {
  const [rows, setRows] = useState(DEFAULT_USE_CHARGE_ROWS);
  const [result, setResult] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/cacfp/allocation`, { withCredentials: true })
      .then(({ data }) => { if (data?.allocation_pct) setAllocation(data); })
      .catch(() => {});
    axios.get(`${API}/cacfp/use-charge`, { withCredentials: true })
      .then(({ data }) => {
        if (data?.rows?.length) {
          setRows(data.rows.map(r => ({ category: r.category, monthly_cost: r.monthly_cost, months_per_year: r.months_per_year })));
          setResult(data);
        }
      }).catch(() => {});
  }, []);

  const updateRow = (i, field, val) => {
    const updated = [...rows];
    updated[i] = { ...updated[i], [field]: field === "category" ? val : Number(val) };
    setRows(updated);
  };

  const handleCalculate = async () => {
    setError(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/cacfp/use-charge`, { rows }, { withCredentials: true });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Calculation failed");
    } finally { setBusy(false); }
  };

  if (!allocation) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3" data-testid="use-charge-no-allocation">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-amber-900">Allocation % not yet set.</p>
          <p className="text-xs text-amber-700 mt-1">Please complete the <strong>Allocation Formula</strong> tab first — its % drives the calculations below.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="use-charge-tab">
      <div className="bg-slate-900 text-white rounded-xl p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-purple-300 mb-2">Formula — FNS § VII D 2 a + Exhibit I</p>
        <p className="text-base font-bold" style={{ fontFamily: "Manrope" }}>CACFP Use Charge for Space</p>
        <p className="text-sm text-slate-200 mt-2 leading-relaxed">
          For each indirect overhead category:<br />
          <strong className="text-purple-300">CACFP Share = (Monthly Cost × Months/Year) × Your Allocation % ({allocation.allocation_pct}%)</strong>
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Indirect Cost Category</th>
                <th className="text-right px-4 py-3 w-32">Monthly Cost</th>
                <th className="text-right px-4 py-3 w-24">Months/Yr</th>
                <th className="text-right px-4 py-3 w-32">Annual Cost</th>
                <th className="text-right px-4 py-3 w-36">CACFP Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const annual = (row.monthly_cost || 0) * (row.months_per_year || 0);
                const share = annual * (allocation.allocation_pct / 100);
                return (
                  <tr key={i} className="border-t border-slate-100" data-testid={`use-charge-row-${i}`}>
                    <td className="px-4 py-2">
                      <input type="text" value={row.category} onChange={(e) => updateRow(i, "category", e.target.value)} className="w-full text-sm border-0 focus:ring-0 focus:outline-none bg-transparent" />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <input type="number" min="0" step="0.01" value={row.monthly_cost} onChange={(e) => updateRow(i, "monthly_cost", e.target.value)} data-testid={`monthly-${i}`} className="w-full text-sm text-right border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-500" />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <input type="number" min="1" max="12" value={row.months_per_year} onChange={(e) => updateRow(i, "months_per_year", e.target.value)} className="w-full text-sm text-right border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-500" />
                    </td>
                    <td className="px-4 py-2 text-right text-slate-500">{fmt2(annual)}</td>
                    <td className="px-4 py-2 text-right font-semibold text-emerald-700">{fmt2(share)}</td>
                  </tr>
                );
              })}
            </tbody>
            {result && (
              <tfoot>
                <tr className="bg-emerald-50 border-t-2 border-emerald-200 font-bold">
                  <td colSpan={4} className="px-4 py-3 text-right text-emerald-900">Total CACFP Use Charge:</td>
                  <td className="px-4 py-3 text-right text-emerald-900 text-base" data-testid="use-charge-total">{fmt2(result.total_cacfp_share)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-slate-500">Using allocation % from your Allocation Formula tab.</p>
          <div className="flex items-center gap-2">
            {result && (
              <button
                onClick={() => downloadPdf(`${API}/cacfp/use-charge/pdf`, `cacfp-use-charge-${new Date().toISOString().slice(0,10)}.pdf`)}
                data-testid="use-charge-download-pdf"
                className="flex items-center gap-2 border-2 border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 text-sm font-bold px-4 py-2 rounded-lg transition-all"
              >
                <Download className="w-4 h-4" /> Download PDF
              </button>
            )}
            {result && (
              <ArchiveButton
                taxYear={new Date().getFullYear()}
                category="CACFP Use Charge"
                source="/api/cacfp/use-charge/pdf"
                sourceParams={{}}
                testId="cacfp-use-charge"
              />
            )}
            <button
              onClick={handleCalculate}
              disabled={busy}
              data-testid="use-charge-calculate-btn"
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white text-sm font-bold px-5 py-2 rounded-lg transition-all flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              {busy ? "Calculating…" : "Calculate & Save"}
            </button>
          </div>
        </div>
      </div>

      {error && typeof error === "string" && (
        <p className="text-xs text-red-600" data-testid="use-charge-error">{error}</p>
      )}

      <Tips>
        <li>Only <strong>indirect</strong> costs allocate by %. Direct CACFP costs (e.g., food) claim 100% — not here.</li>
        <li>Depreciation requires prior State Agency approval (FNS § VIII I 13) before being claimed.</li>
        <li>Save your worksheet before tax season — retain 3 years past fiscal year close (FNS § IX E).</li>
      </Tips>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 4 — Monthly Ledger (state-submission format mirror — Feb 2026 first cut)
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Tab 4 — Monthly Ledger (per-month editable grid, FY Oct→Sept)
// 4 editable rows × 12 months. Each cell saves to /api/cacfp/ledger/entry.
// CACFP Use Charge for Space row is read-only (auto-flows from Use Charge tab).
// ─────────────────────────────────────────────────────────────────────────────
const LEDGER_MONTHS = ["Oct", "Nov", "Dec", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep"];
const LEDGER_ROWS = [
  { category: "CACFP Income", classification: "direct", role: "income", color: "emerald" },
  { category: "CACFP Food Expense", classification: "direct", role: "expense", color: "orange" },
  { category: "CACFP Non-Food Expense", classification: "direct", role: "expense", color: "blue" },
  { category: "CACFP Utilities", classification: "indirect", role: "expense", color: "purple" },
];

function LedgerTab() {
  const now = new Date();
  const defaultFy = now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear();
  const [fy, setFy] = useState(defaultFy);
  const [entries, setEntries] = useState({});       // { "CACFP Income|Jan": amount }
  const [cellStatus, setCellStatus] = useState({}); // { "...|Jan": "saving"|"saved"|"error" }
  const [useChargeAnnual, setUseChargeAnnual] = useState(0);
  const [foodLaborYtd, setFoodLaborYtd] = useState({ food: 0, nonfood: 0, labor: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ledgerRes, ucRes, flRes] = await Promise.all([
        axios.get(`${API}/cacfp/ledger?fiscal_year=${fy}`, { withCredentials: true }),
        axios.get(`${API}/cacfp/use-charge`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/cacfp/food-labor?fiscal_year=${fy}`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      const map = {};
      (ledgerRes.data?.entries || []).forEach(r => {
        map[`${r.category}|${r.month}`] = r.amount;
      });
      setEntries(map);
      setUseChargeAnnual(Number(ucRes.data?.total_cacfp_share || 0));
      setFoodLaborYtd(flRes.data?.ytd || { food: 0, nonfood: 0, labor: 0 });
    } catch {
      setEntries({});
    } finally {
      setLoading(false);
    }
  }, [fy]);

  useEffect(() => { load(); }, [load]);

  const saveCell = async (category, classification, month, value) => {
    const key = `${category}|${month}`;
    const amount = Number(value);
    if (isNaN(amount)) return;
    setCellStatus(prev => ({ ...prev, [key]: "saving" }));
    try {
      if (amount === 0) {
        await axios.delete(`${API}/cacfp/ledger/entry?fiscal_year=${fy}&month=${month}&category=${encodeURIComponent(category)}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/cacfp/ledger/entry`, {
          fiscal_year: fy, month, category, classification, amount,
        }, { withCredentials: true });
      }
      setEntries(prev => ({ ...prev, [key]: amount }));
      setCellStatus(prev => ({ ...prev, [key]: "saved" }));
      setTimeout(() => setCellStatus(prev => { const n = { ...prev }; delete n[key]; return n; }), 1500);
    } catch {
      setCellStatus(prev => ({ ...prev, [key]: "error" }));
    }
  };

  const rowTotal = (cat) => LEDGER_MONTHS.reduce((s, m) => s + Number(entries[`${cat}|${m}`] || 0), 0);
  const colTotal = (month) => LEDGER_ROWS.filter(r => r.role === "expense").reduce((s, r) => s + Number(entries[`${r.category}|${month}`] || 0), 0);
  const colTotalIncome = (month) => Number(entries[`CACFP Income|${month}`] || 0);

  if (loading) return <div className="h-40 bg-slate-100 rounded-xl animate-pulse" data-testid="ledger-loading" />;

  const totalIncome = rowTotal("CACFP Income");
  const totalFood = rowTotal("CACFP Food Expense");
  const totalNonFood = rowTotal("CACFP Non-Food Expense");
  const totalUtilities = rowTotal("CACFP Utilities");
  const totalExpenses = totalFood + totalNonFood + totalUtilities + useChargeAnnual;
  const reimbursementDiff = totalIncome - totalExpenses;
  const foodLaborConflict = (foodLaborYtd.food > 0 || foodLaborYtd.nonfood > 0) && (totalFood > 0 || totalNonFood > 0);

  return (
    <div className="space-y-6" data-testid="ledger-tab">
      <div className="bg-slate-900 text-white rounded-xl p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-purple-300 mb-2">Monthly Ledger — CACFP Fiscal Year (Oct → Sept)</p>
        <p className="text-base font-bold" style={{ fontFamily: "Manrope" }}>State-submission-style detail ledger — editable per month</p>
        <p className="text-sm text-slate-200 mt-2 leading-relaxed">
          Mirrors the FNS-compliant detail-ledger format used for state submissions. Enter your CACFP income and expenses month-by-month. Cells save automatically on blur. The <strong>Use Charge</strong> row pulls from your Allocation worksheet — change it in the Use Charge tab.
        </p>
      </div>

      {/* FY selector + Download branded PDF */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="ledger-fy-row">
        <label className="text-sm font-semibold text-slate-700">Fiscal Year:</label>
        <select value={fy} onChange={(e) => setFy(Number(e.target.value))} data-testid="ledger-fy-select" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
          {[defaultFy - 1, defaultFy, defaultFy + 1].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-xs text-slate-500 italic">FY {fy} runs Oct {fy - 1} → Sept {fy}</span>
        <button
          onClick={() => downloadPdf(`${API}/cacfp/ledger/pdf?fiscal_year=${fy}`, `cacfp-monthly-ledger-fy${fy}.pdf`)}
          data-testid="ledger-download-pdf"
          className="ml-auto inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Download Branded PDF
        </button>
        <ArchiveButton
          taxYear={fy}
          category="CACFP Monthly Ledger"
          source="/api/cacfp/ledger/pdf"
          sourceParams={{ fiscal_year: fy }}
          titleSuffix={`FY ${fy}`}
          testId="cacfp-ledger"
        />
      </div>

      {/* Double-entry warning if both Food&Labor and Ledger have food/nonfood */}
      {foodLaborConflict && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-start gap-3" data-testid="ledger-double-entry-warning">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Possible double-entry detected.</strong> You've entered Food/Non-Food in <strong>both</strong> the Food & Labor Tracker (Tab 5) AND this Monthly Ledger (Tab 4) for FY {fy}. Auto-wire to your Dashboard will add <strong>both</strong> sets. Recommend keeping vendor detail in Tab 5 and entering only Utilities + Income in Tab 4.
          </div>
        </div>
      )}

      {/* Editable per-month grid */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="ledger-grid">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase font-semibold">
              <tr>
                <th className="text-left px-3 py-3 sticky left-0 bg-slate-50 border-r border-slate-200 z-10 min-w-[180px]">Category</th>
                {LEDGER_MONTHS.map(m => (
                  <th key={m} className="text-right px-2 py-3 w-[88px]">{m}</th>
                ))}
                <th className="text-right px-3 py-3 bg-slate-100 border-l border-slate-200 w-[100px]">YTD</th>
              </tr>
            </thead>
            <tbody>
              {LEDGER_ROWS.map(row => (
                <tr key={row.category} className="border-t border-slate-100" data-testid={`ledger-row-${row.category.replace(/\s+/g, "-")}`}>
                  <td className="px-3 py-2 sticky left-0 bg-white border-r border-slate-200 z-10">
                    <div className="font-semibold text-slate-800 text-xs">{row.category}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">
                      {row.role === "income" ? "Income" : (row.classification === "indirect" ? "Indirect" : "Direct")}
                    </div>
                  </td>
                  {LEDGER_MONTHS.map(m => {
                    const key = `${row.category}|${m}`;
                    const status = cellStatus[key];
                    return (
                      <td key={m} className="px-1 py-1">
                        <div className="relative">
                          <input
                            type="number"
                            step="0.01"
                            defaultValue={entries[key] || ""}
                            placeholder="0"
                            onBlur={(e) => {
                              const v = e.target.value === "" ? 0 : Number(e.target.value);
                              const current = entries[key] || 0;
                              if (v !== current) saveCell(row.category, row.classification, m, v);
                            }}
                            data-testid={`ledger-cell-${row.category.replace(/\s+/g, "-")}-${m}`}
                            className={`w-full text-right text-xs px-1.5 py-1 rounded border ${
                              status === "saving" ? "border-blue-300 bg-blue-50" :
                              status === "error" ? "border-red-400 bg-red-50" :
                              status === "saved" ? "border-emerald-400 bg-emerald-50" :
                              "border-slate-200 hover:border-slate-400 focus:border-purple-500"
                            } focus:outline-none focus:ring-1 focus:ring-purple-300`}
                          />
                          {status === "saving" && <Loader2 className="absolute right-1 top-1 w-3 h-3 animate-spin text-blue-500" />}
                        </div>
                      </td>
                    );
                  })}
                  <td className={`px-3 py-2 text-right font-bold border-l border-slate-200 bg-slate-50 text-${row.color}-700`} data-testid={`ledger-ytd-${row.category.replace(/\s+/g, "-")}`}>
                    {fmt2(rowTotal(row.category))}
                  </td>
                </tr>
              ))}

              {/* Use Charge — read-only from worksheet */}
              <tr className="border-t border-slate-100 bg-slate-50/50" data-testid="ledger-row-use-charge">
                <td className="px-3 py-2 sticky left-0 bg-slate-50/50 border-r border-slate-200 z-10">
                  <div className="font-semibold text-slate-600 text-xs italic">CACFP Use Charge for Space</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Indirect • Auto from Worksheet</div>
                </td>
                {LEDGER_MONTHS.map(m => (
                  <td key={m} className="px-2 py-2 text-right text-xs text-slate-400 italic">
                    {useChargeAnnual > 0 ? fmt2(useChargeAnnual / 12) : "—"}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-bold border-l border-slate-200 bg-slate-100 text-slate-700">
                  {fmt2(useChargeAnnual)}
                </td>
              </tr>

              {/* Column totals (expense subtotal per month) */}
              <tr className="border-t-2 border-slate-300 bg-slate-100" data-testid="ledger-col-totals">
                <td className="px-3 py-2 sticky left-0 bg-slate-100 border-r border-slate-200 z-10 font-bold text-slate-800 text-xs">Monthly Expense Subtotal</td>
                {LEDGER_MONTHS.map(m => (
                  <td key={m} className="px-2 py-2 text-right font-bold text-xs text-slate-800">
                    {fmt2(colTotal(m) + (useChargeAnnual / 12))}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-extrabold border-l border-slate-200 bg-slate-200 text-slate-900">
                  {fmt2(totalExpenses)}
                </td>
              </tr>
              <tr className="bg-emerald-50">
                <td className="px-3 py-2 sticky left-0 bg-emerald-50 border-r border-emerald-200 z-10 font-bold text-emerald-800 text-xs">Monthly Income Subtotal</td>
                {LEDGER_MONTHS.map(m => (
                  <td key={m} className="px-2 py-2 text-right font-bold text-xs text-emerald-700">
                    {fmt2(colTotalIncome(m))}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-extrabold border-l border-emerald-200 bg-emerald-100 text-emerald-900">
                  {fmt2(totalIncome)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* YTD reconciliation banner */}
      <div className={`rounded-xl p-5 border-2 ${reimbursementDiff >= 0 ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`} data-testid="ledger-reconciliation">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-600">YTD Reimbursement Difference</p>
            <p className={`text-3xl font-extrabold mt-1 ${reimbursementDiff >= 0 ? "text-emerald-700" : "text-red-700"}`} style={{ fontFamily: "Manrope" }}>
              {fmt2(reimbursementDiff)}
            </p>
            <p className="text-xs text-slate-600 mt-1">
              {reimbursementDiff >= 0
                ? "CACFP income covers your operating expenses."
                : "Operating expenses exceed CACFP income — the difference may be deductible elsewhere on Schedule C."}
            </p>
          </div>
          <div className="text-right text-xs space-y-1">
            <p><span className="text-slate-500">Total Income:</span> <strong className="text-emerald-700">{fmt2(totalIncome)}</strong></p>
            <p><span className="text-slate-500">Total Operating:</span> <strong className="text-slate-900">{fmt2(totalExpenses)}</strong></p>
          </div>
        </div>
      </div>

      <Tips>
        <li>Tab 4 is for state-submission-style monthly reporting. Each cell saves on blur. Empty cells (or 0) delete the entry.</li>
        <li><strong>Use Charge</strong> is auto-divided across 12 months from your annual worksheet — to change it, edit the Use Charge tab.</li>
        <li>Categories are restricted to CACFP-prefixed names so they auto-merge cleanly into your Dashboard / Tax Prep totals.</li>
        <li>If you use the Food & Labor Tracker (Tab 5), do NOT also enter Food / Non-Food in this tab — values will double-count.</li>
      </Tips>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 5 — Food & Labor Tracker (vendor entry by month + YTD reconciliation)
// ─────────────────────────────────────────────────────────────────────────────
function FoodLaborTab() {
  const now = new Date();
  const defaultFy = now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear();
  const [fy, setFy] = useState(defaultFy);
  const [data, setData] = useState(null);
  const [synthetic, setSynthetic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newRow, setNewRow] = useState({ month: "Oct", vendor: "", food: "", nonfood: "", labor: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [showManual, setShowManual] = useState(false);
  const [expandedVendor, setExpandedVendor] = useState(null);

  const months = ["Oct", "Nov", "Dec", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep"];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [manual, syn] = await Promise.all([
        axios.get(`${API}/cacfp/food-labor?fiscal_year=${fy}`, { withCredentials: true }),
        axios.get(`${API}/cacfp/food-labor/synthetic?fiscal_year=${fy}`, { withCredentials: true }),
      ]);
      setData(manual.data);
      setSynthetic(syn.data);
    } catch { setData(null); setSynthetic(null); }
    finally { setLoading(false); }
  }, [fy]);

  useEffect(() => { load(); }, [load]);

  const addRow = async () => {
    setErr("");
    if (!newRow.vendor.trim()) { setErr("Vendor name required."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/cacfp/food-labor/entry`, {
        fiscal_year: fy,
        month: newRow.month,
        vendor: newRow.vendor.trim(),
        food: Number(newRow.food) || 0,
        nonfood: Number(newRow.nonfood) || 0,
        labor: Number(newRow.labor) || 0,
      }, { withCredentials: true });
      setNewRow({ month: newRow.month, vendor: "", food: "", nonfood: "", labor: "" });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const deleteRow = async (r) => {
    if (!confirm(`Delete ${r.vendor} (${r.month})?`)) return;
    try {
      await axios.delete(`${API}/cacfp/food-labor/entry?fiscal_year=${fy}&month=${r.month}&vendor=${encodeURIComponent(r.vendor)}`, { withCredentials: true });
      await load();
    } catch { /* ignore */ }
  };

  if (loading) return <div className="h-40 bg-slate-100 rounded-xl animate-pulse" />;

  // Synthetic data is the primary source — auto-aggregated from transactions + manual entries
  const ytd = synthetic?.ytd || { food: 0, nonfood: 0, labor: 0, total: 0 };
  const reimb = synthetic?.reimbursement_ytd || 0;
  const excess = synthetic?.expenses_exceeding_cacfp || 0;
  const vendors = synthetic?.vendors || [];

  return (
    <div className="space-y-6" data-testid="food-labor-tab">
      <div className="bg-slate-900 text-white rounded-xl p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-purple-300 mb-2">Food & Labor Cost Tracker</p>
        <p className="text-base font-bold" style={{ fontFamily: "Manrope" }}>Auto-aggregated from your Transactions + manual entries</p>
        <p className="text-sm text-slate-200 mt-2 leading-relaxed">
          This view auto-populates from CACFP-categorized transactions you uploaded (bank statements, payroll reports, food invoices).
          Vendor cards below are grouped by vendor, sorted by month within each vendor. Use the "Add Manual Cash Entry" button only for cash transactions
          that have no receipt or payroll record.
        </p>
      </div>

      {/* FY selector + actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm font-semibold text-slate-700">Fiscal Year:</label>
        <select value={fy} onChange={(e) => setFy(Number(e.target.value))} data-testid="fl-fy-select" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
          {[defaultFy - 1, defaultFy, defaultFy + 1].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button
          onClick={() => downloadPdf(`${API}/cacfp/food-labor/pdf?fiscal_year=${fy}`, `cacfp-food-labor-fy${fy}.pdf`)}
          data-testid="food-labor-download-pdf"
          className="ml-auto inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Download Branded PDF
        </button>
        <ArchiveButton
          taxYear={fy}
          category="CACFP Food & Labor"
          source="/api/cacfp/food-labor/pdf"
          sourceParams={{ fiscal_year: fy }}
          titleSuffix={`FY ${fy}`}
          testId="cacfp-food-labor"
        />
      </div>

      {/* YTD totals + reconciliation */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="fl-ytd-row">
        {[
          { label: "YTD Food", val: ytd.food || 0, color: "text-orange-700" },
          { label: "YTD Non-Food", val: ytd.nonfood || 0, color: "text-blue-700" },
          { label: "YTD Labor", val: ytd.labor || 0, color: "text-emerald-700" },
          { label: "YTD Total", val: ytd.total || 0, color: "text-slate-900" },
          { label: "CACFP Reimbursement YTD", val: reimb, color: "text-purple-700" },
        ].map(k => (
          <div key={k.label} className="bg-white border border-slate-200 rounded-xl p-3 text-center">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{k.label}</p>
            <p className={`text-lg font-extrabold mt-1 ${k.color}`}>{fmt2(k.val)}</p>
          </div>
        ))}
      </div>

      <div className={`rounded-xl p-4 ${excess > 0 ? "bg-red-50 border border-red-200" : "bg-emerald-50 border border-emerald-200"}`} data-testid="fl-excess-card">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-600">Expenses Exceeding CACFP Reimbursement</p>
        <p className={`text-3xl font-extrabold mt-1 ${excess > 0 ? "text-red-700" : "text-emerald-700"}`} style={{ fontFamily: "Manrope" }}>
          {fmt2(excess)}
        </p>
        <p className="text-xs text-slate-500 mt-1">{excess > 0 ? "You spent more than CACFP reimbursed — the difference may be deductible elsewhere." : "Reimbursement covers your costs."}</p>
      </div>

      {/* Vendor cards section (synthetic) */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="fl-vendors-section">
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <div>
            <p className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>Vendors ({synthetic?.vendor_count || 0})</p>
            <p className="text-xs text-slate-500">Grouped by vendor · sorted by month within each vendor</p>
          </div>
        </div>

        {vendors.length === 0 ? (
          <div className="px-5 py-10 text-center" data-testid="fl-empty-state">
            <p className="text-slate-600 font-semibold mb-1">No CACFP transactions yet for FY {fy}</p>
            <p className="text-xs text-slate-500 max-w-xl mx-auto leading-relaxed">
              Upload your bank statement, payroll report, or food invoices on the <strong>Transactions</strong> page.
              Rows categorized as <em>CACFP Food Expense</em>, <em>CACFP Non-Food Expense</em>, or <em>CACFP Labor Expense</em> will appear here automatically.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {vendors.map((v, i) => {
              const isOpen = expandedVendor === v.vendor;
              return (
                <div key={v.vendor + i} data-testid={`fl-vendor-${i}`}>
                  <button
                    onClick={() => setExpandedVendor(isOpen ? null : v.vendor)}
                    className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-slate-400 text-sm">{isOpen ? "▼" : "▶"}</span>
                      <span className="font-semibold text-slate-900 text-sm truncate" title={v.vendor}>{v.vendor}</span>
                      {v.has_manual && (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">manual</span>
                      )}
                      <span className="text-xs text-slate-500">· {v.months.length} month{v.months.length === 1 ? "" : "s"}</span>
                    </div>
                    <span className="text-sm font-bold text-slate-900 flex-shrink-0">{fmt2(v.total)}</span>
                  </button>
                  {isOpen && (
                    <div className="bg-slate-50 px-5 py-3 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="text-left py-1.5 px-2">Month</th>
                            <th className="text-right py-1.5 px-2">Food</th>
                            <th className="text-right py-1.5 px-2">Non-Food</th>
                            <th className="text-right py-1.5 px-2">Labor</th>
                            <th className="text-right py-1.5 px-2">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {v.months.map((m, j) => (
                            <tr key={j} className="border-t border-slate-200">
                              <td className="py-1.5 px-2 font-semibold text-slate-700">{m.month}</td>
                              <td className="py-1.5 px-2 text-right text-orange-700">{fmt2(m.food)}</td>
                              <td className="py-1.5 px-2 text-right text-blue-700">{fmt2(m.nonfood)}</td>
                              <td className="py-1.5 px-2 text-right text-emerald-700">{fmt2(m.labor)}</td>
                              <td className="py-1.5 px-2 text-right font-bold text-slate-900">{fmt2(m.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Manual cash entry (fallback) — collapsed by default */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <button
          onClick={() => setShowManual(s => !s)}
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 text-left"
          data-testid="fl-manual-toggle"
        >
          <div>
            <p className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>Add Manual Cash Entry</p>
            <p className="text-xs text-slate-500">Use only for cash transactions with no receipt or payroll record. Most entries should come from Transactions/Upload.</p>
          </div>
          <span className="text-slate-400 text-xs">{showManual ? "Hide" : "Show"}</span>
        </button>
        {showManual && (
          <div className="px-5 py-4 border-t border-slate-100" data-testid="fl-add-form">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <select value={newRow.month} onChange={(e) => setNewRow({ ...newRow, month: e.target.value })} data-testid="fl-month" className="border border-slate-300 rounded-lg px-2 py-2 text-sm">
                {months.map(m => <option key={m}>{m}</option>)}
              </select>
              <input type="text" placeholder="Source/Vendor" value={newRow.vendor} onChange={(e) => setNewRow({ ...newRow, vendor: e.target.value })} data-testid="fl-vendor" className="border border-slate-300 rounded-lg px-3 py-2 text-sm col-span-2" />
              <input type="number" step="0.01" placeholder="Food $" value={newRow.food} onChange={(e) => setNewRow({ ...newRow, food: e.target.value })} data-testid="fl-food" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <input type="number" step="0.01" placeholder="Non-Food $" value={newRow.nonfood} onChange={(e) => setNewRow({ ...newRow, nonfood: e.target.value })} data-testid="fl-nonfood" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              <input type="number" step="0.01" placeholder="Labor $" value={newRow.labor} onChange={(e) => setNewRow({ ...newRow, labor: e.target.value })} data-testid="fl-labor" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <button onClick={addRow} disabled={saving} data-testid="fl-add-btn" className="mt-3 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white text-sm font-bold px-5 py-2 rounded-lg flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Saving…" : "Add Entry"}
            </button>
            {err && <p className="text-xs text-red-600 mt-2" data-testid="fl-error">{String(err)}</p>}

            {/* Existing manual entries list */}
            {(data?.entries || []).length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-600 uppercase font-semibold">
                    <tr>
                      <th className="text-left px-3 py-2">Month</th>
                      <th className="text-left px-3 py-2">Source</th>
                      <th className="text-right px-3 py-2">Food</th>
                      <th className="text-right px-3 py-2">Non-Food</th>
                      <th className="text-right px-3 py-2">Labor</th>
                      <th className="text-right px-3 py-2">Total</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.entries || []).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`fl-row-${i}`}>
                        <td className="px-3 py-2 text-slate-700 font-semibold">{r.month}</td>
                        <td className="px-3 py-2 text-slate-700">{r.vendor}</td>
                        <td className="px-3 py-2 text-right text-orange-700">{fmt2(r.food)}</td>
                        <td className="px-3 py-2 text-right text-blue-700">{fmt2(r.nonfood)}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">{fmt2(r.labor)}</td>
                        <td className="px-3 py-2 text-right font-bold text-slate-900">{fmt2(r.total)}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => deleteRow(r)} data-testid={`fl-del-${i}`} className="text-xs text-red-500 hover:text-red-700 font-semibold">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <Tips>
        <li>Most entries flow in automatically from your uploaded bank statement, payroll report, or food invoices on the Transactions page.</li>
        <li>YTD totals auto-flow into CACFP Overview and Tax Prep Document.</li>
        <li>Labor charges require after-the-fact tracking (FNS § VIII I 23 c(3)) — budgets not allowed.</li>
        <li>"Expenses Exceeding CACFP" = what your operation costs above reimbursement (often deductible on Schedule C).</li>
      </Tips>
    </div>
  );
}
